import {
   createContext,
   useState,
   useEffect,
   useContext,
   useReducer,
} from 'react'
import _has from 'lodash/has'
import _isEmpty from 'lodash/isEmpty'
import {
   useSubscription,
   useMutation,
   useApolloClient,
} from '@apollo/react-hooks'
import { useToasts } from 'react-toast-notifications'
import { useRouter } from 'next/router'

import {
   GET_CART_PAYMENT_INFO,
   UPDATE_CART_PAYMENT,
   CREATE_PRINT_JOB,
   UPDATE_CART,
   CUSTOMER,
   CART_VALIDITY,
} from '../graphql'
import { useUser, useCart, useTranslation } from '../context'
import { useConfig } from '../lib'
import {
   getRazorpayOptions,
   isClient,
   useRazorPay,
   usePaytm,
   usePineLabsTerminalPay,
   useTerminalPay,
   getPaytmOptions,
   get_env,
   isKiosk,
} from '../utils'
import {
   CartPaymentComponent,
   PaymentProcessingModal,
   PrintProcessingModal,
} from '../components'
import { ConversationInstance } from 'twilio/lib/rest/conversations/v1/conversation'

const PaymentContext = createContext()
const initialState = {
   profileInfo: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
   },
   paymentInfo: {
      tunnel: {
         isVisible: false,
      },
      selectedAvailablePaymentOption: null,
   },
   paymentLoading: false,
   paymentLifeCycleState: '',
   printDetails: {
      isPrintInitiated: false,
      printStatus: 'not-started',
      message: '',
   },
   onPaymentSuccessCallback: () => {},
   onPaymentCancelCallback: () => {},
}

const reducer = (state, action) => {
   switch (action.type) {
      case 'SET_PROFILE_INFO':
         return {
            ...state,
            profileInfo: action.payload,
         }
      case 'SET_PAYMENT_INFO':
         return {
            ...state,
            paymentInfo: {
               ...state.paymentInfo,
               ...action.payload,
            },
         }
      case 'UPDATE_INITIAL_STATE':
         return {
            ...state,
            ...action.payload,
         }
      default:
         return state
   }
}

export const PaymentProvider = ({ children }) => {
   const router = useRouter()
   const [state, dispatch] = useReducer(reducer, initialState)
   const [isPaymentLoading, setIsPaymentLoading] = useState(true)
   const [cartId, setCartId] = useState(null)
   const [cartPayment, setCartPayment] = useState(null)
   const [cartPaymentId, setCartPaymentId] = useState(null)
   const [isProcessingPayment, setIsProcessingPayment] = useState(false)
   const [isPaymentInitiated, setIsPaymentInitiated] = useState(false)
   //State to disable pay button to prevent accidental double click
   const [updatingCartPayment, setUpdatingCartPayment] = useState(false)
   const { user, isAuthenticated, isLoading } = useUser()
   const {
      kioskDetails,
      settings,
      selectedOrderTab,
      configOf,
      KioskConfig,
      currentPage,
      setCurrentPage,
      setPaymentProcessingModalVisible,
   } = useConfig()
   const { displayRazorpay } = useRazorPay()
   const { displayPaytm } = usePaytm()
   const {
      initiateTerminalPayment,
      byPassTerminalPayment,
      cancelTerminalPayment,
   } = useTerminalPay()
   const {
      initiateTerminalPayment: initiatePineLabsTerminalPayment,
      cancelTerminalPayment: cancelPineLabsTerminalPayment,
   } = usePineLabsTerminalPay()
   const { cartState, checkIsCartValidByProductAvailability } = useCart()
   const { addToast } = useToasts()
   const { t } = useTranslation()
   const apolloClient = useApolloClient()

   const BY_PASS_TERMINAL_PAYMENT = get_env('BY_PASS_TERMINAL_PAYMENT')
   const brand = settings?.brand?.[`Brand Info`]
   const theme = settings?.Visual?.[`theme-color`]?.themeColor
   const kioskSettings = KioskConfig

   const printReceipt =
      kioskSettings?.printPopupSettings?.initiatePrintingAfterPosistOrderCreated
         ?.value ?? false
   // subscription to get cart payment info
   const {
      data: { cartPayments: cartPaymentsFromQuery = [] } = {},
      error: hasCartPaymentError,
      loading: isCartPaymentLoading,
   } = useSubscription(GET_CART_PAYMENT_INFO, {
      skip: !cartPaymentId && !cartId, // When cartId is not available use cartPaymentId to get the cartPayments
      fetchPolicy: 'no-cache',
      variables: {
         where: {
            isResultShown: {
               _eq: false,
            },
            ...(cartPaymentId
               ? {
                    id: {
                       _eq: cartPaymentId,
                    },
                 }
               : {
                    cartId: {
                       _eq: cartId,
                    },
                 }),
         },
      },
   })

   useSubscription(CART_VALIDITY, {
      skip: !cartId,
      variables: {
         id: cartId,
      },
      onSubscriptionData: async data => {
         if (!data.subscriptionData.data?.cart) return
         if (!data.subscriptionData.data?.cart?.isCartValidOnCheckout) {
            await apolloClient.mutate({
               mutation: UPDATE_CART,
               variables: {
                  id: cartId,
                  _set: {
                     isCartValidOnCheckout: true,
                  },
               },
            })
            setIsPaymentInitiated(false)
            setIsProcessingPayment(false)
            addToast('Something went wrong', { appearance: 'error' })
         }
      },
      fetchPolicy: 'no-cache',
   })

   // mutation to update cart payment
   const [updateCartPayment] = useMutation(UPDATE_CART_PAYMENT, {
      onError: error => {
         console.error(error)
         addToast(`${t('Something went wrong')}`, { appearance: 'error' })
      },
   })

   // mutation to update cart
   const [updateCart] = useMutation(UPDATE_CART, {
      onError: error => {
         console.error(error)
         addToast(`${t('Something went wrong')}`, { appearance: 'error' })
      },
   })

   // mutation to create print job
   const [createPrintJob, { loading: isPrinting, error: hasPrintError }] =
      useMutation(CREATE_PRINT_JOB, {
         onCompleted: ({ createPrintJob = {} }) => {
            if (createPrintJob.success) {
               dispatch({
                  type: 'UPDATE_INITIAL_STATE',
                  payload: {
                     printDetails: {
                        ...state.printDetails,
                        printStatus: 'success',
                     },
                  },
               })
            } else {
               dispatch({
                  type: 'UPDATE_INITIAL_STATE',
                  payload: {
                     printDetails: {
                        ...state.printDetails,
                        printStatus: 'failed',
                        message: createPrintJob.message,
                     },
                  },
               })
            }
         },
         onError: error => {
            console.error(error)
            addToast(`${t('Something went wrong')}`, { appearance: 'error' })
            dispatch({
               type: 'UPDATE_INITIAL_STATE',
               payload: {
                  printDetails: {
                     ...state.printDetails,
                     printStatus: 'failed',
                     message: `${t(
                        'Something went wrong while printing please try again!'
                     )}`,
                  },
               },
            })
         },
      })

   //<---------  methods to set/update reducer state  --------->

   const setProfileInfo = profileInfo => {
      dispatch({
         type: 'SET_PROFILE_INFO',
         payload: profileInfo,
      })
   }

   const setPaymentInfo = paymentInfo => {
      dispatch({
         type: 'SET_PAYMENT_INFO',
         payload: paymentInfo,
      })
   }

   const updatePaymentState = state => {
      dispatch({
         type: 'UPDATE_INITIAL_STATE',
         payload: state,
      })
   }

   const onCancelledHandler = () => {
      if (!_isEmpty(cartPayment)) {
         updateCartPayment({
            variables: {
               id: cartPayment?.id,
               _set: {
                  paymentStatus: 'CANCELLED',
                  comment:
                     'Cancelled by user using back button or dismiss modal',
               },
               _inc: {
                  cancelAttempt: 1,
               },
            },
         })
      }
      // Calling onPaymentCancel Callback which is passed in PaymentOptionRenderer Component
      state.onPaymentCancelCallback()
   }
   const onPaymentModalClose = async () => {
      await updateCartPayment({
         variables: {
            id: cartPayment?.id,
            _set: {
               ...(!['SUCCEEDED', 'FAILED', 'CANCELLED'].includes(
                  cartPayment?.paymentStatus
               ) && {
                  paymentStatus: 'FAILED',
               }),
               isResultShown: true,
            },
         },
      })
   }

   const retryPayment = async () => {
      await updateCartPayment({
         variables: {
            id: cartPayment?.id,
            _set: {
               ...(!['SUCCEEDED', 'FAILED', 'CANCELLED'].includes(
                  cartPayment?.paymentStatus
               ) && {
                  paymentStatus: 'FAILED',
               }),
               isResultShown: true,
            },
         },
      })
      await updateCart({
         variables: {
            id: cartPayment?.cartId,
            _set: {},
            _inc: {
               paymentRetryAttempt: 1,
            },
         },
      })
   }

   const eventHandler = async response => {
      dispatch({
         type: 'UPDATE_INITIAL_STATE',
         payload: {
            isPaymentProcessing: false,
            isPaymentInitiated: false,
            paymentLifeCycleState: '',
         },
      })
   }

   const initializePayment = async (
      requiredCartId,
      cartPaymentId,
      paymentLifeCycleState = 'INITIALIZE'
   ) => {
      if (requiredCartId) {
         setCartId(requiredCartId)
      } else if (cartPaymentId) {
         setCartPaymentId(cartPaymentId)
      }
      setIsPaymentInitiated(true)
      setIsProcessingPayment(true)
      dispatch({
         type: 'UPDATE_INITIAL_STATE',
         payload: {
            paymentLifeCycleState,
         },
      })
   }

   const initializePrinting = async () => {
      await dispatch({
         type: 'UPDATE_INITIAL_STATE',
         payload: {
            printDetails: {
               isPrintInitiated: true,
               printStatus: 'ongoing',
               message: '',
            },
         },
      })
      if (!_isEmpty(settings) && isClient) {
         const path =
            settings['printing']?.['KioskCustomerTokenTemplate']?.path?.value
         const DATA_HUB_HTTPS = get_env('DATA_HUB_HTTPS')
         const { origin } = new URL(DATA_HUB_HTTPS)
         const templateOptions = encodeURI(
            JSON.stringify({
               path,
               format: 'raw',
               readVar: false,
            })
         )
         const templateVariable = encodeURI(
            JSON.stringify({
               cartId: cartState?.cart?.id,
               paymentMode:
                  cartPayment?.availablePaymentOption?.supportedPaymentOption
                     ?.paymentOptionLabel === 'TERMINAL'
                     ? 'card'
                     : 'counter',
               orderType: selectedOrderTab?.orderFulfillmentTypeLabel || 'N/A',
            })
         )
         const url = `${origin}/template/?template=${templateOptions}&data=${templateVariable}`

         await createPrintJob({
            variables: {
               contentType: 'raw_uri',
               printerId: kioskDetails?.printerId,
               source: 'Dailykit',
               title: `TOKEN-${cartPayment?.cartId}`,
               url,
            },
         })
      }
      // await dispatch({
      //    type: 'UPDATE_INITIAL_STATE',
      //    payload: {
      //       printDetails: {
      //          ...state.printDetails,
      //          isPrintInitiated: true,
      //          printStatus: 'success',
      //       },
      //    },
      // })
   }

   const setPrintStatus = value => {
      dispatch({
         type: 'UPDATE_INITIAL_STATE',
         payload: {
            printDetails: {
               ...state.printDetails,
               printStatus: value,
            },
         },
      })
   }

   const resetPaymentProviderStates = async () => {
      await dispatch({
         type: 'UPDATE_INITIAL_STATE',
         payload: {
            profileInfo: {
               firstName: '',
               lastName: '',
               phoneNumber: '',
               email: '',
            },
            paymentInfo: {
               tunnel: {
                  isVisible: false,
               },
               selectedAvailablePaymentOption: null,
            },
            paymentLoading: false,
            paymentLifeCycleState: '',
            printDetails: {
               isPrintInitiated: false,
               printStatus: 'not-started',
               message: '',
            },
         },
      })
      setCartId(null)
      setCartPayment(null)
      setIsPaymentLoading(true)
      setIsPaymentInitiated(false)
      setIsProcessingPayment(false)
      setUpdatingCartPayment(false)
   }

   const getCustomerInfo = async keycloakId => {
      let customerData = await apolloClient.query({
         query: CUSTOMER.PROFILE_INFO,
         variables: {
            keycloakId: keycloakId,
         },
      })
      customerData = customerData.data?.customer?.brandCustomers[0]
      return customerData
   }

   //<---------  methods to set/update reducer state  --------->

   // setting cartPayment in state
   useEffect(() => {
      if (!cartId && !cartPaymentId) {
         setCartId(cartState?.cart?.id || null)
      }
      if (isKiosk()) {
         if (currentPage === 'paymentPage') {
            if (!_isEmpty(cartPaymentsFromQuery)) {
               setCartPayment(cartPaymentsFromQuery[0])
               setCartId(cartPaymentsFromQuery[0].cartId)
               setIsPaymentInitiated(true)
               setIsProcessingPayment(true)
            } else {
               setCartPayment(null)
            }
         }
      } else {
         if (!_isEmpty(cartPaymentsFromQuery)) {
            setCartPayment(cartPaymentsFromQuery[0])
            setCartId(cartPaymentsFromQuery[0].cartId)
            setIsPaymentInitiated(true)
            setIsProcessingPayment(true)
         } else {
            setCartPayment(null)
         }
      }
   }, [cartPaymentsFromQuery])

   // setting user related info in payment provider context
   useEffect(() => {
      if (
         isAuthenticated &&
         !_isEmpty(user) &&
         _has(user, 'platform_customer') &&
         _has(user, 'brandCustomers') &&
         !isLoading
      ) {
         dispatch({
            type: 'SET_PROFILE_INFO',
            payload: {
               firstName: user.brandCustomers?.firstName || '',
               lastName: user.brandCustomers?.lastName || '',
               email: user.brandCustomers?.email || '',
               phoneNumber: user.brandCustomers?.phoneNumber || '',
            },
         })

         dispatch({
            type: 'SET_PAYMENT_INFO',
            payload: {
               selectedAvailablePaymentOption: {
                  ...state.paymentInfo.selectedAvailablePaymentOption,
                  selectedPaymentMethodId:
                     user.platform_customer?.defaultPaymentMethodId || null,
               },
               paymentMethods: user.platform_customer?.paymentMethods,
            },
         })
         setIsPaymentLoading(false)
      }
   }, [user])

   // initiating payment flow (this is required after coming back from paytm payment page)
   useEffect(() => {
      if (!_isEmpty(router.query) && _has(router.query, 'payment')) {
         setIsPaymentInitiated(true)
         setIsProcessingPayment(true)
         if (_has(router.query, 'id') && router.query.id) {
            setCartId(router.query.id)
         } else if (_has(router.query, 'paymentId') && router.query.paymentId) {
            setCartPaymentId(router.query.paymentId)
            let updatedURL = new URL(
               `${window.location.origin}${router.asPath}`
            )
            updatedURL.searchParams.delete('payment')
            updatedURL.searchParams.delete('paymentId')
            router.replace(
               `${updatedURL.pathname}${updatedURL.search}`,
               undefined,
               { shallow: true }
            )
         }
      }
   }, [router.query])

   useEffect(() => {
      if (cartPayment?.paymentStatus === 'SUCCEEDED') {
         if (printReceipt) {
            initializePrinting()
         }
         setCurrentPage('paymentSuccessPage')
         setPaymentProcessingModalVisible(false)
         // Calling onPaymentCancel Callback which is passed in PaymentOptionRenderer Component
         state.onPaymentSuccessCallback()
      }
   }, [cartPayment?.paymentStatus])

   // useEffect which checks the payment company and payment related status and does required actions
   useEffect(() => {
      if (
         isPaymentInitiated &&
         !_isEmpty(cartPayment) &&
         _has(
            cartPayment,
            'availablePaymentOption.supportedPaymentOption.supportedPaymentCompany.label'
         ) &&
         !isCartPaymentLoading
      ) {
         setIsProcessingPayment(true)
         // right now only handle the razorpay method.
         if (
            cartPayment.availablePaymentOption.supportedPaymentOption
               .supportedPaymentCompany.label === 'razorpay'
         ) {
            if (
               cartPayment.paymentStatus === 'CREATED' &&
               !_isEmpty(cartPayment?.stripeInvoiceId)
            ) {
               ;(async () => {
                  const options = getRazorpayOptions({
                     orderDetails: cartPayment.transactionRemark,
                     brand,
                     theme,
                     paymentInfo: cartPayment.availablePaymentOption,
                     profileInfo:
                        cartPayment?.cart?.customerInfo ||
                        (await getCustomerInfo(
                           cartPayment?.metaData.customerKeycloakId
                        )),
                     ondismissHandler: () => onCancelledHandler(),
                     eventHandler,
                  })
                  if (state.paymentLifeCycleState === 'INITIALIZE') {
                     await displayRazorpay(options)
                  }
               })()
            }
         } else if (
            cartPayment.availablePaymentOption.supportedPaymentOption
               .supportedPaymentCompany.label === 'paytm'
         ) {
            if (['PENDING', 'PROCESSING'].includes(cartPayment.paymentStatus)) {
               if (isClient && cartPayment.actionRequired) {
                  const paymentUrl = cartPayment.actionUrl
                  window.location.href = paymentUrl
               }
            }
         } else if (
            cartPayment.availablePaymentOption.supportedPaymentOption
               .paymentOptionLabel === 'TERMINAL'
         ) {
            if (cartPayment.paymentStatus === 'PENDING') {
               ;(async () => {
                  await initiateTerminalPayment(cartPayment)
               })()
            }
         } else if (
            cartPayment.availablePaymentOption.supportedPaymentOption
               .paymentOptionLabel === 'PINE_LABS_TERMINAL'
         ) {
            if (cartPayment.paymentStatus === 'PENDING') {
               ;(async () => {
                  await initiatePineLabsTerminalPayment(cartPayment)
               })()
            }
         }
      }
   }, [
      cartPayment?.paymentStatus,
      cartPayment?.transactionId,
      cartPayment?.stripeInvoiceId,
      cartPayment?.actionUrl,
      isCartPaymentLoading,
   ])

   return (
      <PaymentContext.Provider
         value={{
            state,
            paymentLoading: isPaymentLoading,
            setPaymentInfo,
            setProfileInfo,
            setIsProcessingPayment,
            setIsPaymentInitiated,
            updatePaymentState,
            initializePayment,
            isProcessingPayment,
            initializePrinting,
            resetPaymentProviderStates,
            setUpdatingCartPayment,
            updatingCartPayment,
         }}
      >
         {isPaymentInitiated && (
            <PaymentProcessingModal
               isOpen={isProcessingPayment}
               cartPayment={cartPayment}
               cartId={cartState?.cart?.id}
               closeModal={onPaymentModalClose}
               retryPayment={retryPayment}
               normalModalClose={resetPaymentProviderStates}
               cancelPayment={onCancelledHandler}
               isTestingByPass={BY_PASS_TERMINAL_PAYMENT === 'true'}
               byPassTerminalPayment={byPassTerminalPayment}
               cancelTerminalPayment={cancelTerminalPayment}
               PaymentOptions={cartState.kioskPaymentOptions}
               initializePrinting={initializePrinting}
               resetPaymentProviderStates={resetPaymentProviderStates}
               setIsProcessingPayment={setIsProcessingPayment}
               setIsPaymentInitiated={setIsPaymentInitiated}
               setUpdatingCartPayment={setUpdatingCartPayment}
               updatingCartPayment={updatingCartPayment}
            />
         )}
         {state.printDetails.isPrintInitiated && (
            <PrintProcessingModal
               printDetails={state.printDetails}
               setPrintStatus={setPrintStatus}
               resetPaymentProviderStates={resetPaymentProviderStates}
               initializePrinting={initializePrinting}
            />
         )}

         {children}
      </PaymentContext.Provider>
   )
}

export const usePayment = () => {
   const {
      state,
      paymentLoading,
      setPaymentInfo,
      setProfileInfo,
      setIsProcessingPayment,
      setIsPaymentInitiated,
      updatePaymentState,
      initializePayment,
      isProcessingPayment,
      resetPaymentProviderStates,
      setUpdatingCartPayment,
      updatingCartPayment,
   } = useContext(PaymentContext)
   return {
      isPaymentLoading: paymentLoading,
      isPaymentProcessing: state.isPaymentProcessing,
      isPaymentSuccess: state.isPaymentSuccess,
      isPaymentDismissed: state.isPaymentDismissed,
      paymentLifeCycleState: state.paymentLifeCycleState,
      profileInfo: state.profileInfo,
      paymentInfo: state.paymentInfo,
      setPaymentInfo: setPaymentInfo,
      setProfileInfo: setProfileInfo,
      setIsProcessingPayment,
      setIsPaymentInitiated,
      updatePaymentState,
      initializePayment,
      isProcessingPayment,
      resetPaymentProviderStates,
      setUpdatingCartPayment,
      updatingCartPayment,
   }
}
